/**
 * Filter drawer.
 *
 * Toggles the storefront filter tray that drops down from the "Filters" control
 * on the collection page. The form inside it is an ordinary GET form that
 * navigates, so once the tray is open nothing here is involved in filtering.
 *
 * NOT MODAL, and that is deliberate — unlike assets/cart-drawer.js there is no
 * focus trap, no page-inert, no scroll lock and no wash over the page. A
 * drop-down tray is a disclosure, not a dialog: Tab should walk out the far side
 * of it, and the page behind stays usable. Same call assets/mega-menu.js makes.
 *
 * WHERE THE WORK IS SPLIT
 *
 *   CSS/Liquid — the drop, the shadow, the columns, the form and every string.
 *                See snippets/filter-drawer.liquid.
 *   here       — the state, and the two things state drags along with it: the
 *                ARIA contract and the `inert` attribute.
 *
 * ONE STATE ATTRIBUTE. `data-open` goes on <filter-drawer> and nowhere else. The
 * panel reads it through `group-data-open/filter:`.
 *
 * NO SECTION-RENDERING, NO AJAX, DELIBERATELY. Applying a filter is a real
 * navigation. It keeps the filter state in the URL where it is shareable and
 * restorable by the back button, and it means the tray has no way to show a
 * product list that disagrees with the address bar.
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK: section JS was
 * measured NOT running at all on this store, and it cost three rounds of
 * debugging on the header. Order-independent — it touches no shared state.
 */
if (!customElements.get('filter-drawer')) {
  customElements.define(
    'filter-drawer',
    class FilterDrawer extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('[data-filter-panel]');
        this.trigger = this.querySelector('[data-filter-trigger]');
        this.closeButton = this.querySelector('[data-filter-close]');

        /**
         * Fail loudly and leave the closed state alone. The panel keeps the
         * `inert` it was rendered with, so a broken wiring is a tray that never
         * opens rather than a block of controls nobody can see but a screen
         * reader still walks through.
         */
        if (!this.panel || !this.trigger) {
          console.warn('[filter-drawer] markup is incomplete; filters disabled');
          return;
        }

        this.isOpen = false;

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);

        this.trigger.addEventListener('click', this.handleTriggerClick);

        /**
         * Close sits across the control row from Filters and is `hidden` until
         * the tray opens, so it is never in the tab order while it would do
         * nothing. The control itself still toggles — this is a second way out,
         * not the only one.
         */
        this.closeButton?.addEventListener('click', this.handleCloseClick);

        /**
         * The ARIA is set here rather than in the markup because without this
         * script the button does nothing at all, and a control advertising
         * `aria-expanded` that can never change is a lie told to exactly the
         * visitors least able to recover from it.
         *
         * `aria-expanded` on a disclosure, NOT `aria-haspopup="dialog"` — this is
         * not a dialog, and announcing it as one would promise a focus trap and a
         * modal context that deliberately do not exist.
         */
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.setAttribute('aria-controls', this.panel.id || 'FilterPanel');
        if (!this.panel.id) this.panel.id = 'FilterPanel';

        document.addEventListener('keydown', this.handleKeydown);

        /**
         * `pointerdown`, not `click`. A click that begins inside the tray and
         * ends outside it — releasing the mouse after dragging to select the text
         * of a filter label — fires `click` on an ancestor of both, which reads
         * as an outside click and would shut the tray mid-selection.
         */
        document.addEventListener('pointerdown', this.handleDocumentPointerDown);
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.handleTriggerClick);
        this.closeButton?.removeEventListener('click', this.handleCloseClick);
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
      }

      /* ------------------------------------------------------------------ */
      /* Open / close                                                        */
      /* ------------------------------------------------------------------ */

      handleTriggerClick(event) {
        event.preventDefault();
        this.setOpen(!this.isOpen);
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.close();

        /**
         * Close goes `display: none` the instant the tray shuts, so focus has to
         * be handed somewhere deliberate or the browser drops it to <body>. The
         * control it sits across from is the obvious place.
         *
         * setOpen()'s own rescue cannot cover this: it tests
         * `this.contains(document.activeElement)` AFTER flipping `data-open`, by
         * which point Close is already hidden and activeElement is already
         * <body>. Hence the explicit call here.
         */
        this.trigger.focus();
      }

      open() {
        this.setOpen(true);
      }

      close() {
        this.setOpen(false);
      }

      /** The single funnel. Nothing else touches state. */
      setOpen(next) {
        if (next === this.isOpen) return;
        this.isOpen = next;

        this.toggleAttribute('data-open', next);
        this.panel.toggleAttribute('inert', !next);
        this.trigger.setAttribute('aria-expanded', String(next));

        /**
         * FOCUS IS NOT MOVED ON OPEN. The tray sits directly after the control in
         * the DOM, so Tab walks into it on its own — which is what a disclosure
         * should do, and it avoids the focus ring a scripted `.focus()` leaves on
         * mouse users.
         *
         * On CLOSE it has to be rescued, though: the panel becomes `inert`, and
         * if focus was inside it the browser drops it to <body> and a keyboard
         * user loses their place on the page entirely.
         */
        if (!next && this.contains(document.activeElement)) {
          this.trigger.focus();
        }
      }

      /* ------------------------------------------------------------------ */
      /* Dismissal                                                           */
      /* ------------------------------------------------------------------ */

      handleKeydown(event) {
        if (event.key !== 'Escape' || !this.isOpen) return;

        this.close();

        /**
         * Escape always returns focus to the control, even when focus was
         * already outside the tray — Escape is a deliberate act, and leaving the
         * caret wherever it happened to be makes the tray feel like it swallowed
         * the key.
         */
        this.trigger.focus();
      }

      handleDocumentPointerDown(event) {
        if (!this.isOpen) return;
        if (!(event.target instanceof Node)) return;

        /** The trigger is inside the host, so its own toggle is covered here. */
        if (this.contains(event.target)) return;

        this.close();
      }
    }
  );
}
